import React, { useState } from "react";
import useSWR from "swr";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, MapPin } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { Skeleton, StatusPill } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function MgmtProjectDetail() {
  const { id } = useParams();
  const { data } = useSWR(`/management/projects/${id}`, fetcher);
  const [subIdx, setSubIdx] = useState(0);

  if (!data) return <Skeleton h={400}/>;
  const sub = data.subprojects[subIdx];

  return (
    <div className="space-y-5">
      <Link to="/management/projects" className="rc-btn rc-btn-sm rc-btn-ghost -ml-3"><ChevronLeft size={16}/> Projects</Link>

      <div className="rc-card rc-card-padded">
        <div className="rc-title-1">{data.project.project_name}</div>
        <div className="rc-footnote flex items-center gap-1 mt-1"><MapPin size={12}/>{data.project.location}</div>
      </div>

      <div className="flex gap-2 overflow-x-auto rc-noscroll">
        {data.subprojects.map((s, i) => (
          <button key={s.subproject_id} onClick={() => setSubIdx(i)}
            className={`rc-btn rc-btn-sm ${i===subIdx?"rc-btn-primary":"rc-btn-secondary"}`} data-testid={`sub-tab-${i}`}>{s.subproject_name}</button>
        ))}
      </div>

      {sub && (
        <>
          <div className="rc-card rc-card-padded">
            <div className="flex items-center justify-between mb-3">
              <div className="rc-title-3">Flat grid · {sub.subproject_name}</div>
              <div className="flex gap-3 rc-footnote">
                <span className="flex items-center gap-1"><span className="rc-flat rc-flat-booked !w-4 !h-4 !rounded-sm"></span> Booked</span>
                <span className="flex items-center gap-1"><span className="rc-flat rc-flat-available !w-4 !h-4 !rounded-sm"></span> Available</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {sub.flats.map(f => (
                <div key={f.flat_id} className={`rc-flat rc-flat-${f.status}`} title={`${f.flat_number}${f.customer_name ? " — " + f.customer_name : ""}`} data-testid={`flat-${f.flat_id}`}>
                  {f.flat_number}
                </div>
              ))}
              {sub.flats.length === 0 && <div className="rc-footnote">No flats in this tower yet.</div>}
            </div>
          </div>

          <div className="rc-card rc-card-padded">
            <div className="rc-title-3 mb-3">Milestones</div>
            <div className="divide-y divide-[var(--rc-line)]">
              {sub.milestones.map(m => (
                <div key={m.milestone_id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0"><div className="rc-headline truncate">{m.milestone_name}</div><div className="rc-footnote">Planned {fmtDate(m.planned_date)}</div></div>
                  <div className="flex items-center gap-3">
                    <div className="rc-headline rc-tabular">{m.completion_pct}%</div>
                    <StatusPill status={m.status}/>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rc-card rc-card-padded">
            <div className="rc-title-3 mb-3">Open tickets — this project</div>
            <div className="divide-y divide-[var(--rc-line)]">
              {data.open_tickets.slice(0, 10).map(t => (
                <Link to={`/crm/tickets/${t.ticket_id}`} key={t.ticket_id} className="py-3 flex items-center justify-between gap-3 hover:bg-[var(--rc-near-white)] -mx-2 px-2 rounded-lg">
                  <div className="min-w-0"><div className="rc-headline truncate">{t.subject}</div><div className="rc-caption">#{t.ticket_id.slice(0,8)} · {t.category}</div></div>
                  <StatusPill status={t.sla_breached ? "breached" : t.status}/>
                </Link>
              ))}
              {data.open_tickets.length === 0 && <div className="rc-footnote py-4 text-center">All clear.</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
